/**
 * O status de um serviço como os documentos o mostram: o selo do `VoucherDocument` e,
 * ao lado do "Aguardando aprovação" dele e da faixa do voucher aéreo, QUEM precisa aprovar.
 */
import type { VoucherDocumentTone, VoucherDocumentView } from './views/voucher_document_view.js';
import type { DocumentContext } from './context.js';
import type { TripService } from './services.js';
import { voucherStatusPill, type VoucherBadgeVariant } from './status.js';

/** As cinco variantes do pill do painel → o quarteto semântico do genui2. */
const TONE: Record<VoucherBadgeVariant, VoucherDocumentTone> = {
  confirmed: 'success',
  progress: 'neutral',
  amber: 'warning',
  cancelled: 'danger',
  gray: 'neutral',
};

/**
 * "Aguardando Maria Souza e João Lima" — quem o viajante cobra pela aprovação (#4459).
 * Só no "Aguardando aprovação" e só com nome: sem aprovador nomeado a linha não nasce, como
 * todo campo sem dado do documento. A lista é a do idioma (`Intl.ListFormat`).
 */
function awaitingApproversLabel(service: TripService, ctx: DocumentContext): string | null {
  if (service.status !== 'AWAITING_APPROVAL') return null;
  const names = (service.voucher.pendingApprovers ?? []).map((approver) => approver.name);
  if (names.length === 0) return null;
  const list = new Intl.ListFormat(ctx.locale, { type: 'conjunction' }).format(names);
  return ctx.t('trips.voucher.awaitingApprovers', { names: list });
}

/** O selo de status do documento — `null` quando o status não tem pill. */
export function documentStatus(
  service: TripService,
  ctx: DocumentContext,
): VoucherDocumentView['status'] {
  const pill = voucherStatusPill(service.status, service.voucher);
  if (!pill) return null;
  const detail = awaitingApproversLabel(service, ctx);
  return {
    label: ctx.t(pill.labelKey),
    tone: TONE[pill.variant],
    ...(detail ? { detail } : {}),
  };
}
